import get from 'lodash/get';

export const UNSOLICITED_EFFECT_TYPES = [
  'UNSOLICITED_CALL',
  'UNSOLICITED_DIALOGUE',
  'AI_UNSOLICITED_DIALOGUE',
  'NPC_UNSOLICITED_DIALOGUE',
  'NPC_AI_UNSOLICITED_DIALOGUE',
];

export const PATIENT = 'patient';

// Which character speaks the line.
export function speakerOf(effect) {
  if (!effect || !UNSOLICITED_EFFECT_TYPES.includes(effect.type)) {
    return null;
  }
  if (effect.type === 'UNSOLICITED_CALL') {
    return effect.callId || null;
  }
  return effect.npcKey || effect.sourceCharacterKey || PATIENT;
}

// Owns the wait-for-previous flag. Only offered where the Cases Service can
// honor it: a same-character unsolicited line wired straight into this node.
//
// Hosts supply `waitForPreviousSpeaker` and `update()`, call
// loadWaitForPrevious(data) where they read saved data, and render/persist
// `waitForPrevious`.
const waitForPreviousMixins = {
  data() {
    return {
      waitForPrevious: false,
      hasUnsolicitedPredecessor: false,
      // Only edits after the first pass count as becoming eligible.
      waitForPreviousEvaluated: false,
    };
  },
  watch: {
    // Repointing the speaker requalifies every edge.
    waitForPreviousSpeaker() {
      this.refreshUnsolicitedPredecessor();
    },
  },
  mounted() {
    // A mixin's mounted runs before the host's, which is where state lands.
    this.$nextTick(this.refreshUnsolicitedPredecessor);
    // Edge changes never fire 'process' on this control, and a predecessor
    // swapping effect type or speaker only ever fires it on that other node.
    this.emitter.on(
      'connectioncreated connectionremoved noderemoved process',
      () => this.$nextTick(this.refreshUnsolicitedPredecessor),
    );
  },
  methods: {
    loadWaitForPrevious(data) {
      // New nodes default on; legacy nodes are set by migration.
      this.waitForPrevious = data ? data.waitForPrevious === true : true;
    },
    selectWaitForPrevious(waitForPrevious) {
      this.waitForPrevious = !!waitForPrevious;
      this.update();
    },
    refreshUnsolicitedPredecessor() {
      const node = get(this.control, 'parent');
      // Rete's emitter has no 'off', so the control this node just replaced
      // keeps listening. Detached from its node, it can only corrupt the one
      // that took its place.
      if (!node) {
        return;
      }
      const speaker = this.waitForPreviousSpeaker;
      const inputs = Array.from(node.inputs.values());
      const wasEligible = this.hasUnsolicitedPredecessor;
      const firstPass = !this.waitForPreviousEvaluated;
      this.waitForPreviousEvaluated = true;

      // One qualifying edge is enough -- the service splices per connection.
      this.hasUnsolicitedPredecessor = !!speaker && inputs.some(input =>
        input.connections.some((connection) => {
          const source = get(connection, 'output.node');
          return !!source
            && source.name === 'Effect'
            && speakerOf(get(source, 'data.effect')) === speaker;
        }));

      if (!this.hasUnsolicitedPredecessor) {
        // The service silently ignores a wait it cannot honor.
        this.setWaitForPrevious(false);
      } else if (!firstPass && !wasEligible) {
        // Newly wired, so take the default. Not on load -- a stored false is an answer.
        this.setWaitForPrevious(true);
      }
    },
    setWaitForPrevious(waitForPrevious) {
      if (this.waitForPrevious === waitForPrevious) {
        return;
      }
      this.waitForPrevious = waitForPrevious;
      // Persisting an unsaved node would publish a half-filled effect.
      if (this.getData(this.ikey)) {
        this.update();
      }
    },
  },
};

export default waitForPreviousMixins;
